import { useEffect, useState, useCallback } from 'react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import CentroCustoSelect from '../components/CentroCustoSelect'
import ItemSelect from '../components/ItemSelect'
import { useEmpresa } from '../contexts/EmpresaContext'

// Consulta de Itens das Notas de Entrada — equivalente web da WCCO011E legada.
// Mesma base da Consulta de Notas (WCCO011B), só que no nível do item, uma
// linha por item lançado. Filtros simplificados a pedido do usuário: sem CFOP,
// Conta Aplicação e Conta Estoque (existem no legado, tirados aqui).

interface LinhaItem {
  empcod: number
  serie: string
  numero: number
  ftvTipdco: string
  modelo: string
  dataEmissao: string | null
  dataRecebimento: string | null
  cliCod: string
  fornecedor: string
  pnvLin: number
  itsCod: number
  descricao: string
  tnoCod: number | null
  destCod: string
  veiCod: string
  tipSerCod: number | null
  ccuCod: string
  quantidade: number
  valorItem: number
  valorUnitario: number
}

interface Filtros {
  empcodDe: string
  empcodAte: string
  dataDe: string
  dataAte: string
  tipoData: 'recebimento' | 'emissao'
  ftvTipdco: string
  ftvTipdcoDes: string
  modelo: string
  modeloDes: string
  tnoCod: string
  tnoDes: string
  ccuCod: string
  ccuDes: string
  destCod: string
  destDes: string
  cprCod: string
  cprDes: string
  cliCod: string
  cliDes: string
  itsCod: string
  itsDes: string
  itsUn: string
  veiCod: string
  veiDes: string
  tipSerCod: string
  tipSerDes: string
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }

function fmtData(iso: string | null): string {
  if (!iso || iso.slice(0, 4) <= '0001') return '-'
  return iso.slice(0, 10).split('-').reverse().join('/')
}
function fmtVal(v: number): string {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
}

// Badge colorido pro código do Tipo de Documento (Doc) — cores fixas pros mais
// comuns (NFE/NSE/CTE), cinza neutro pros demais.
const DOC_COR: Record<string, { fundo: string; texto: string }> = {
  NFE: { fundo: '#dbeafe', texto: '#1e40af' },
  NSE: { fundo: '#ede9fe', texto: '#6d28d9' },
  CTE: { fundo: '#fef3c7', texto: '#92400e' },
}
function badgeDoc(doc: string) {
  const c = DOC_COR[doc] ?? { fundo: '#f1f5f9', texto: '#475569' }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: 10,
      fontSize: '0.76rem', fontWeight: 700, background: c.fundo, color: c.texto,
    }}>{doc || '-'}</span>
  )
}

// Chip cinza neutro pra códigos curtos (Nat/Mod/Dest/Veículo/Servi/C.Cus) —
// só pra dar uma "textura" visual e separar do texto corrido da linha.
function chip(valor: string | number | null) {
  if (valor === null || valor === undefined || valor === '') return <span style={{ color: '#cbd5e1' }}>-</span>
  return (
    <span style={{
      display: 'inline-block', padding: '1px 6px', borderRadius: 5,
      fontSize: '0.76rem', fontWeight: 600, fontFamily: 'monospace',
      background: '#eef2f7', color: '#475569',
    }}>{valor}</span>
  )
}

function Grupo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label style={{
        fontSize: '0.66rem', fontWeight: 700, color: '#1a6ea8',
        textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        {children}
      </div>
    </div>
  )
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.82rem', height: 30, background: '#fff',
}
const th: React.CSSProperties = {
  padding: '6px 8px', textAlign: 'left', color: '#fff', fontWeight: 700,
  fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
}
const td: React.CSSProperties = { padding: '6px 8px', color: '#374151', whiteSpace: 'nowrap', fontSize: '0.82rem' }

export default function ConsultaItens() {
  const { empresaCod } = useEmpresa()

  function filtroPadrao(): Filtros {
    const emp = String(empresaCod ?? 1)
    return {
      empcodDe: emp, empcodAte: emp,
      dataDe: hojeISO(), dataAte: hojeISO(), tipoData: 'recebimento',
      ftvTipdco: '', ftvTipdcoDes: '', modelo: '', modeloDes: '',
      tnoCod: '', tnoDes: '', ccuCod: '', ccuDes: '', destCod: '', destDes: '',
      cprCod: '', cprDes: '', cliCod: '', cliDes: '',
      itsCod: '', itsDes: '', itsUn: '', veiCod: '', veiDes: '', tipSerCod: '', tipSerDes: '',
    }
  }

  const [lista, setLista] = useState<LinhaItem[]>([])
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const [filtros, setFiltros] = useState<Filtros>(filtroPadrao)
  const [pesquisaAtiva, setPesquisaAtiva] = useState<Filtros | null>(null)

  function paramsDeFiltro(f: Filtros): Record<string, string> {
    const params: Record<string, string> = {}
    if (f.empcodDe) params.empcodDe = f.empcodDe
    if (f.empcodAte) params.empcodAte = f.empcodAte
    if (f.dataDe) params.dataDe = f.dataDe
    if (f.dataAte) params.dataAte = f.dataAte
    params.tipoData = f.tipoData
    if (f.ftvTipdco) params.ftvTipdco = f.ftvTipdco
    if (f.modelo) params.modelo = f.modelo
    if (f.tnoCod) params.tnoCod = f.tnoCod
    if (f.ccuCod) params.ccuCod = f.ccuCod
    if (f.destCod) params.destCod = f.destCod
    if (f.cprCod) params.cprCod = f.cprCod
    if (f.cliCod) params.cliCod = f.cliCod
    if (f.itsCod) params.itsCod = f.itsCod
    if (f.veiCod) params.veiCod = f.veiCod
    if (f.tipSerCod) params.tipSerCod = f.tipSerCod
    return params
  }

  const carregar = useCallback(async () => {
    if (!pesquisaAtiva) return
    setCarregando(true); setErro(null)
    try {
      const r = await api.get('/erp/itens-entrada/consulta', { params: paramsDeFiltro(pesquisaAtiva) })
      setLista(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao consultar itens.')
    } finally {
      setCarregando(false)
    }
  }, [pesquisaAtiva])

  useEffect(() => { carregar() }, [carregar])
  useEffect(() => {
    const p = filtroPadrao()
    setFiltros(p)
    setPesquisaAtiva(p)
  }, [empresaCod]) // eslint-disable-line react-hooks/exhaustive-deps

  function pesquisar() { setPesquisaAtiva({ ...filtros }) }
  function limpar() { const p = filtroPadrao(); setFiltros(p); setPesquisaAtiva(p) }

  return (
    <div className="card">
      <h2 style={{
        fontSize: '1.1rem', fontWeight: 700, margin: '0 0 1rem', color: '#1a6ea8',
        paddingBottom: '0.6rem', borderBottom: '2px solid #eff6ff',
      }}>
        Consulta de Itens das Notas de Entrada
      </h2>

      <div style={{
        background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
        borderRadius: '0.5rem', padding: '0.7rem 0.9rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem 1.4rem', alignItems: 'flex-end' }}>
            <Grupo label="Empresa">
              <input style={{ ...inp, width: 34, textAlign: 'center' }} type="text" inputMode="numeric" maxLength={2}
                value={filtros.empcodDe}
                onChange={e => setFiltros(f => ({ ...f, empcodDe: e.target.value.replace(/\D/g, '').slice(0, 2) }))} />
              <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>a</span>
              <input style={{ ...inp, width: 34, textAlign: 'center' }} type="text" inputMode="numeric" maxLength={2}
                value={filtros.empcodAte}
                onChange={e => setFiltros(f => ({ ...f, empcodAte: e.target.value.replace(/\D/g, '').slice(0, 2) }))} />
            </Grupo>
            <Grupo label="Data Inicial">
              <input style={{ ...inp, width: 140 }} type="date" value={filtros.dataDe}
                onChange={e => setFiltros(f => ({ ...f, dataDe: e.target.value }))} />
            </Grupo>
            <Grupo label="Data Final">
              <input style={{ ...inp, width: 140 }} type="date" value={filtros.dataAte}
                onChange={e => setFiltros(f => ({ ...f, dataAte: e.target.value }))} />
            </Grupo>
            <Grupo label="Tipo Data">
              <select style={{ ...inp, width: 130 }} value={filtros.tipoData}
                onChange={e => setFiltros(f => ({ ...f, tipoData: e.target.value as Filtros['tipoData'] }))}>
                <option value="recebimento">Recebimento</option>
                <option value="emissao">Emissão</option>
              </select>
            </Grupo>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem 1.4rem', alignItems: 'flex-end', paddingTop: '0.6rem', borderTop: '1px solid #e5eaf3' }}>
            <Grupo label="Documento">
              <div style={{ width: 90 }}>
                <LookupSelect
                  endpoint="/erp/doccad/lista"
                  codKey="doc_cod" descKey="doc_des" tagKey="doc_cod"
                  codValue={filtros.ftvTipdco} descValue={filtros.ftvTipdco}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, ftvTipdco: cod, ftvTipdcoDes: des }))}
                  placeholder="Todos..." listWidth={260}
                />
              </div>
            </Grupo>
            <Grupo label="Modelo">
              <div style={{ width: 90 }}>
                <LookupSelect
                  endpoint="/erp/moddoc/lista"
                  codKey="moddoc_cod" descKey="moddoc_des" tagKey="moddoc_cod"
                  codValue={filtros.modelo} descValue={filtros.modeloDes} valorExibido={filtros.modelo}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, modelo: cod, modeloDes: des }))}
                  placeholder="Todos..." listWidth={380}
                />
              </div>
            </Grupo>
            <Grupo label="Natureza">
              <div style={{ width: 90 }}>
                <LookupSelect
                  endpoint="/erp/naturezas-operacao/lista"
                  codKey="tno_cod" descKey="tno_des"
                  renderItem={(item: any) => (
                    <>
                      <div className="nat-item-linha1"><span className="nat-item-cod">{item.tno_cod}</span></div>
                      <div className="nat-item-dsc">{item.tno_des}</div>
                    </>
                  )}
                  codValue={filtros.tnoCod} descValue={filtros.tnoCod}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, tnoCod: cod, tnoDes: des }))}
                  placeholder="Todas..." listWidth={320}
                />
              </div>
            </Grupo>
            <Grupo label="Centro de Custo">
              <div style={{ width: 200 }}>
                <CentroCustoSelect
                  value={filtros.ccuCod}
                  onChange={(cod, des) => setFiltros(f => ({ ...f, ccuCod: cod, ccuDes: des }))}
                />
              </div>
            </Grupo>
            <Grupo label="Destino Material">
              <div style={{ width: 200 }}>
                <LookupSelect
                  endpoint="/erp/oedcad/lista"
                  codKey="oed_cod" descKey="oed_des" tagKey="oed_cod"
                  codValue={filtros.destCod} descValue={filtros.destDes}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, destCod: cod, destDes: des }))}
                  placeholder="Todos..." listWidth={320}
                />
              </div>
            </Grupo>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem 1.4rem', alignItems: 'flex-end', paddingTop: '0.6rem', borderTop: '1px solid #e5eaf3' }}>
            <Grupo label="Comprador">
              <div style={{ width: 200 }}>
                <LookupSelect
                  endpoint="/compradores"
                  codKey="cpr_cod" descKey="cpr_des"
                  codValue={filtros.cprCod} descValue={filtros.cprDes}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, cprCod: cod, cprDes: des }))}
                  placeholder="Todos..." listWidth={260}
                />
              </div>
            </Grupo>
            <Grupo label="Fornecedor">
              <div style={{ width: 240 }}>
                <LookupSelect
                  endpoint="/erp/clientes/lista"
                  codKey="cli_cod" descKey="cli_fan"
                  renderItem={(item: any) => (
                    <>
                      <div className="nat-item-linha1"><span className="nat-item-cod">{item.cli_cod}</span></div>
                      <div className="nat-item-dsc">{item.cli_fan || item.cli_nom}</div>
                    </>
                  )}
                  codValue={filtros.cliCod} descValue={filtros.cliDes}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, cliCod: cod, cliDes: des }))}
                  placeholder="Todos..." listWidth={320} somenteComTexto
                />
              </div>
            </Grupo>
            <Grupo label="Item">
              <div style={{ width: 260 }}>
                <ItemSelect
                  codValue={filtros.itsCod} descValue={filtros.itsDes} unValue={filtros.itsUn}
                  onChange={(cod, des, un) => setFiltros(f => ({ ...f, itsCod: cod, itsDes: des, itsUn: un }))}
                />
              </div>
            </Grupo>
            <Grupo label="Veículo">
              <div style={{ width: 200 }}>
                <LookupSelect
                  endpoint="/erp/veiculos"
                  codKey="veicod" descKey="veipla"
                  buildDesc={(item: any) => `${item.veicod} — ${item.veipla}`}
                  renderItem={(item: any) => (
                    <>
                      <div className="nat-item-linha1"><span className="nat-item-cod">{item.veicod}</span></div>
                      <div className="nat-item-dsc">{item.veipla}</div>
                    </>
                  )}
                  codValue={filtros.veiCod} descValue={filtros.veiDes}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, veiCod: cod, veiDes: des }))}
                  placeholder="Todos..." listWidth={220}
                />
              </div>
            </Grupo>
            <Grupo label="Serviço">
              <div style={{ width: 220 }}>
                <LookupSelect
                  endpoint="/erp/serman/lista"
                  codKey="sercod" descKey="serdes" tagKey="sercod"
                  codValue={filtros.tipSerCod} descValue={filtros.tipSerDes}
                  onSelect={(cod, des) => setFiltros(f => ({ ...f, tipSerCod: cod, tipSerDes: des }))}
                  placeholder="Todos..." listWidth={320}
                />
              </div>
            </Grupo>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem' }}>
          <button className="btn btn-primary btn-sm" onClick={pesquisar} disabled={carregando} style={{ height: 30 }}>
            {carregando ? 'Buscando...' : 'Pesquisar'}
          </button>
          <button className="btn btn-sm" onClick={limpar} disabled={carregando} style={{ height: 30 }}>
            Limpar
          </button>
        </div>
      </div>

      {erro && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 6, padding: '0.6rem 0.9rem', marginBottom: '0.8rem', fontSize: '0.85rem' }}>
          {erro}
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
          <thead>
            <tr style={{ background: '#1a6ea8' }}>
              <th style={th}>D.Entrada</th>
              <th style={{ ...th, textAlign: 'center' }}>Emp</th>
              <th style={{ ...th, textAlign: 'center' }}>Ser</th>
              <th style={th}>N.Fiscal</th>
              <th style={th}>Doc</th>
              <th style={th}>Mod</th>
              <th style={th}>Nat</th>
              <th style={th}>Fornecedor</th>
              <th style={th}>Dest</th>
              <th style={th}>Veículo</th>
              <th style={th}>Servi</th>
              <th style={th}>Item</th>
              <th style={th}>Descrição</th>
              <th style={th}>C.Cus</th>
              <th style={{ ...th, textAlign: 'right' }}>Qtde</th>
              <th style={{ ...th, textAlign: 'right' }}>Val.Item</th>
              <th style={{ ...th, textAlign: 'right' }}>Vl.Uni</th>
            </tr>
          </thead>
          <tbody>
            {carregando && (
              <tr><td colSpan={17} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Carregando...</td></tr>
            )}
            {!carregando && lista.length === 0 && (
              <tr><td colSpan={17} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhum item encontrado</td></tr>
            )}
            {!carregando && lista.map((it, idx) => (
              <tr key={`${it.empcod}-${it.serie}-${it.numero}-${it.cliCod}-${it.pnvLin}`}
                style={{ borderBottom: '1px solid #e5eaf3', background: idx % 2 === 1 ? '#f7faff' : '#fff' }}>
                <td style={{ ...td, color: '#475569', fontWeight: 600 }}>{fmtData(it.dataRecebimento)}</td>
                <td style={{ ...td, textAlign: 'center' }}>{chip(it.empcod)}</td>
                <td style={{ ...td, textAlign: 'center' }}>{chip(it.serie)}</td>
                <td style={{ ...td, fontWeight: 700, color: '#1a6ea8' }}>{it.numero}</td>
                <td style={td}>{badgeDoc(it.ftvTipdco)}</td>
                <td style={td}>{chip(it.modelo)}</td>
                <td style={td}>{chip(it.tnoCod)}</td>
                <td style={{ ...td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600, color: '#1e293b' }} title={it.fornecedor}>{it.fornecedor}</td>
                <td style={td}>{chip(it.destCod)}</td>
                <td style={td}>{chip(it.veiCod)}</td>
                <td style={td}>{chip(it.tipSerCod)}</td>
                <td style={{ ...td, color: '#7c3aed', fontWeight: 700 }}>{it.itsCod}</td>
                <td style={{ ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.descricao}>{it.descricao}</td>
                <td style={td}>{chip(it.ccuCod)}</td>
                <td style={{ ...td, textAlign: 'right', color: '#6b7280' }}>{it.quantidade.toLocaleString('pt-BR', { minimumFractionDigits: 3 })}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmtVal(it.valorItem)}</td>
                <td style={{ ...td, textAlign: 'right', color: '#6b7280' }}>{fmtVal(it.valorUnitario)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
